import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { Home, Search, Download, Eye } from 'lucide-react';
import { useCertificateStore, Certificate } from "@/services/certificateService";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";

const Certificates: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [typeFilter, setTypeFilter] = useState<'Instituição' | 'Gerador de Crédito' | 'Todos'>('Todos');
  
  // Estado para controlar o pop-up de projetos
  const [projetosDialogOpen, setProjetosDialogOpen] = useState(false);
  
  // Usar o store de certificados compartilhado
  const { certificates } = useCertificateStore();
  
  // Função para verificar se a rota atual corresponde a um determinado padrão
  const isActivePath = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    // Para projetos, verificamos várias rotas possíveis
    if (path === 'projects') {
      return ['/my-projects', '/project/status', '/project/region', '/all-projects'].some(
        p => location.pathname.startsWith(p)
      );
    }
    return location.pathname === path;
  };
  
  // Função para navegar para a rota desejada
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Função para lidar com o clique no botão Projetos
  const handleProjetosClick = () => {
    setProjetosDialogOpen(true);
  };
  
  // Filtrar certificados com base no termo de busca
  const filteredCertificates = searchTerm.trim() === '' 
    ? certificates 
    : certificates.filter(cert =>
        (cert as any).email && typeof (cert as any).email === 'string' && (cert as any).email.toLowerCase().includes(searchTerm.toLowerCase())
      );
  const filteredByType = typeFilter === 'Todos'
    ? filteredCertificates
    : filteredCertificates.filter(cert => (cert as any).type === typeFilter);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      
      {/* Menu horizontal superior */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex space-x-6 overflow-x-auto py-3">
          <button 
            className={`${isActivePath('/planos') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/planos')}
          >
            Planos
          </button>
          <button 
            className={`${isActivePath('/dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`${isActivePath('projects') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={handleProjetosClick}
          >
            Projetos
          </button>
          <button 
            className={`${isActivePath('/emission') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/emission')}
          >
            Emissão
          </button>
          <button 
            className={`${isActivePath('/traceability') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/traceability')}
          >
            Rastreabilidade
          </button>
          <button 
            className={`${isActivePath('/certificates') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/certificates')}
          >
            Certificados
          </button>
          <button
            className={`${isActivePath('/pregao-dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/pregao-dashboard')}
          >
            Bolsa de Crédito de Carbono Voluntário
          </button>
          <button 
            className={`${isActivePath('/compliance') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/compliance')}
          >
            Conformidade
          </button>
          <button 
            className={`${isActivePath('/retirement') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/retirement')}
          >
            Aposentadoria
          </button>
          <button 
            className={`${isActivePath('/educational') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/educational')}
          >
            Espaço Educativo
          </button>
        </div>
      </div>
      
      {/* Pop-up de Acesso para Projetos */}
      <Dialog open={projetosDialogOpen} onOpenChange={setProjetosDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Acesso a Projetos</DialogTitle>
            <DialogDescription className="text-center">
              Selecione o tipo de visualização de projetos que deseja acessar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/my-projects');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Meus Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Visualize todos os seus projetos cadastrados</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/status');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Status dos Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acompanhe o status de todos os projetos</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/region');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Projetos por Região</h3>
                <p className="text-sm text-gray-500 text-center">Explore projetos por localização geográfica</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              window.open('https://banclima.com/projetos', '_blank');
              setProjetosDialogOpen(false);
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Portal de Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acesse o portal público de projetos do Banclima</p>
              </CardContent>
            </Card>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button 
              variant="outline" 
              onClick={() => setProjetosDialogOpen(false)}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <div className="flex-grow bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Certificados</h1>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <h2 className="text-lg font-bold text-[#0e7cb4] mb-2">Certificado de Redução de Emissões de GEE</h2>
            <p className="mb-2">Este certificado reconhece e comprova a contribuição efetiva para a redução das emissões de Gases de Efeito Estufa (GEE).</p>
            <p className="mb-2">A emissão foi quantificada com base em metodologias reconhecidas e critérios técnicos confiáveis, validando ações sustentáveis que promovem a mitigação dos impactos ambientais.</p>
            <p>Ao emitir este certificado, a plataforma reforça o compromisso com a transparência, responsabilidade climática e desenvolvimento sustentável.</p>
          </div>
          
          <div className="bg-white p-6 shadow-md rounded-lg mb-6">
            <h2 className="text-lg font-semibold mb-4">Buscar Certificados</h2>
            <div className="flex gap-2 max-w-2xl">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por email"
                className="flex-1"
              />
              <Button>
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </Button>
            </div>
          </div>
          
          {/* Filtro por tipo */}
          <div className="mb-4 flex items-center gap-2">
            <label htmlFor="typeFilter" className="font-medium">Tipo:</label>
            <select
              id="typeFilter"
              className="border rounded px-2 py-1"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value as 'Instituição' | 'Gerador de Crédito' | 'Todos')}
            >
              <option value="Todos">Todos</option>
              <option value="Instituição">Instituição</option>
              <option value="Gerador de Crédito">Gerador de Crédito</option>
            </select>
          </div>
          
          {/* Tabela de certificados */}
          <div className="bg-white p-4 shadow-md rounded-lg">
            <table className="min-w-full text-sm text-left">
              <thead>
                <tr className="border-b">
                  <th className="py-2 px-3">PROJETO</th>
                  <th className="py-2 px-3">CERTIFICADO</th>
                  <th className="py-2 px-3">EMISSÃO</th>
                  <th className="py-2 px-3">ESTADO</th>
                  <th className="py-2 px-3">STATUS</th>
                  <th className="py-2 px-3">AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {filteredByType.map((certificate) => (
                  <tr key={certificate.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">{certificate.project}</td>
                    <td className="py-2 px-3">{certificate.serialNumber}</td>
                    <td className="py-2 px-3">{certificate.date}</td>
                    <td className="py-2 px-3">{certificate.location.state}</td>
                    <td className="py-2 px-3">
                      {certificate.status === 'Active' && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">Certificado Emitido</span>
                      )}
                      {certificate.status === 'Pending' && (
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">Certificado em Andamento</span>
                      )}
                      {certificate.status === 'Revoked' && (
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">Revogado</span>
                      )}
                    </td>
                    <td className="py-2 px-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setSelectedCertificate(certificate)}>
                        <Eye className="h-4 w-4 mr-1" /> Visualizar Certificado
                      </Button>
                      <a href={certificate.downloadUrl} download target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="default">
                          <Download className="h-4 w-4 mr-1" /> Baixar PDF
                        </Button>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredByType.length === 0 && (
            <div className="bg-white p-8 shadow-md rounded-lg text-center">
              <p className="text-gray-600">Nenhum certificado encontrado.</p>
            </div>
          )}
          
          {selectedCertificate && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <h2 className="text-2xl font-bold">{selectedCertificate.name}</h2>
                    <Button variant="outline" onClick={() => setSelectedCertificate(null)}>
                      Fechar
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <img 
                        src={selectedCertificate.project === 'Moeda Social Carbono (2025)' ? '/images/certificado.png' : selectedCertificate.certificateImage} 
                        alt={selectedCertificate.name} 
                        className="w-full h-auto object-cover rounded-lg shadow-md"
                      />
                      <div className="mt-4 flex justify-between">
                        <Button>
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </Button>
                        <Button variant="outline">
                          Verificar Autenticidade
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold">Informações do Certificado</h3>
                        <div className="mt-2 space-y-2">
                          <p><span className="font-semibold">Organização:</span> {selectedCertificate.organization}</p>
                          <p><span className="font-semibold">Projeto:</span> {selectedCertificate.project}</p>
                          <p><span className="font-semibold">Tipo de Crédito:</span> {selectedCertificate.creditType}</p>
                          <p><span className="font-semibold">Quantidade:</span> {selectedCertificate.creditAmount} tCO₂e</p>
                          <p><span className="font-semibold">Data de Emissão:</span> {selectedCertificate.date}</p>
                          <p><span className="font-semibold">Validade:</span> {selectedCertificate.validityPeriod}</p>
                          <p><span className="font-semibold">Status:</span> {selectedCertificate.status}</p>
                          <p><span className="font-semibold">Emissor:</span> {selectedCertificate.issuer}</p>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold">Localização</h3>
                        <p className="mt-2">
                          {selectedCertificate.location.city}, {selectedCertificate.location.state}, {selectedCertificate.location.country}
                        </p>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold">Descrição</h3>
                        <p className="mt-2 text-gray-600">{selectedCertificate.description}</p>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold">Rastreabilidade</h3>
                        <div className="mt-2 space-y-2">
                          <p><span className="font-semibold">Número de Série:</span> {selectedCertificate.serialNumber}</p>
                          <p><span className="font-semibold">Token ID:</span> {selectedCertificate.tokenId}</p>
                          <p><span className="font-semibold">Método de Verificação:</span> {selectedCertificate.verificationMethod}</p>
                          <p>
                            <span className="font-semibold">URL de Verificação:</span> 
                            <a 
                              href={selectedCertificate.verificationURL} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline ml-1"
                            >
                              {selectedCertificate.verificationURL}
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-start my-8 ml-4">
        <h2 className="text-xl font-bold text-[#0e7cb4] mb-2 text-left">Selo de Redução de Emissões de GEE</h2>
        <p className="mb-2 text-left max-w-xl">Este Selo reconhece e comprova a contribuição efetiva para a redução das emissões de Gases de Efeito Estufa (GEE).</p>
        <p className="mb-2 text-left max-w-xl">A emissão foi quantificada com base em metodologias reconhecidas e critérios técnicos confiáveis, validando ações sustentáveis que promovem a mitigação dos impactos ambientais.</p>
        <p className="mb-6 text-left max-w-xl">Ao emitir este Selo, a plataforma reforça o compromisso com a transparência, responsabilidade climática e desenvolvimento sustentável.</p>
        <div className="mt-2">
          <img src="/images/selo-verde.png.png" alt="Selo Verde Carbono Reduzido 2024" className="w-64 h-64 object-contain" />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Certificates; 